// Pure helpers that condense a cycle version payload into readable facts for the
// Overview tab. Every helper returns `null` for a shape it does not recognise, so the
// component can show a translated "details in the report tabs" line — never raw JSON.

import { dailyDose } from './healthReportFormat'

export const TARGET_KEYS = ['calories_kcal', 'protein_g', 'carbs_rest_day_g', 'carbs_training_day_g', 'fat_g', 'fiber_g', 'target_weight_kg']

const isObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const isScalar = (v) => ['string', 'number', 'boolean'].includes(typeof v)

export const valueUnit = (x) => [x?.value ?? '—', x?.unit].filter((p) => p !== null && p !== undefined && p !== '').join(' ')

/** A scalar as text, or a one-level object reduced to its value/name/title. `null` when not readable. */
export const readableValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (isScalar(value)) return String(value)
  if (isObject(value)) {
    if (isScalar(value.value)) return valueUnit(value)
    const label = [value.name, value.title, value.label].find(isScalar)
    if (label !== undefined) return String(label)
    const values = Object.values(value)
    if (values.length && values.length <= 4 && values.every((v) => v === null || isScalar(v))) {
      const text = values.filter((v) => v !== null && v !== '').join(' ')
      return text || '—'
    }
  }
  return null
}

export const nutritionTargetsSummary = (value) => {
  if (!isObject(value)) return null
  const targets = TARGET_KEYS.filter((k) => isObject(value[k])).map((k) => ({ key: k, target: value[k] }))
  const limits = (Array.isArray(value.item_limits) ? value.item_limits : []).filter((l) => isObject(l) && l.code)
  return targets.length || limits.length ? { targets, limits } : null
}

export const decisionState = (rec) =>
  rec.status === 'done' ? 'done' : rec.is_accepted === true ? 'accepted' : rec.is_accepted === false ? 'rejected' : 'open'

export const recommendationsSummary = (value) => {
  if (!Array.isArray(value)) return null
  const items = value.filter((r) => isObject(r) && isScalar(r.title))
  if (items.length !== value.length) return null
  const counts = { accepted: 0, rejected: 0, open: 0, done: 0 }
  items.forEach((r) => { counts[decisionState(r)] += 1 })
  const top = [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  return { total: items.length, counts, top }
}

/** Supplement id → recommended action, read from the lever keys `pause:<id>` / `dose:<id>:<ingredient>`. */
const supplementActions = (recommendations) => {
  const out = {}
  for (const r of Array.isArray(recommendations) ? recommendations : []) {
    const [kind, id] = String(r?.key ?? '').split(':')
    if (kind === 'pause') out[id] = 'pause'
    else if (kind === 'dose' && !out[id]) out[id] = 'change_dose'
  }
  return out
}

export const supplementsSummary = (value, recommendations) => {
  if (!Array.isArray(value)) return null
  if (value.some((s) => !isObject(s) || !isScalar(s.name))) return null
  const actions = supplementActions(recommendations)
  return value.map((s) => ({
    id: s.user_supplement_id ?? s.name,
    name: s.name,
    dose: dailyDose(s),
    action: actions[String(s.user_supplement_id)] ?? null,
  }))
}

const NORMAL_STATUSES = new Set(['normal', 'optimal', 'unknown'])

export const markersSummary = (value) => {
  if (!isObject(value) || !Array.isArray(value.results)) return null
  const results = value.results.filter(isObject)
  const abnormal = results.filter((r) => r.is_abnormal || r.is_critical || (r.status && !NORMAL_STATUSES.has(r.status)))
  return {
    normalCount: results.length - abnormal.length,
    abnormal: abnormal.map((r) => ({
      id: r.result_id ?? r.marker_code ?? r.marker_name,
      name: r.marker_name || r.marker_code || '—',
      value: [r.value_operator, r.value ?? r.value_text, r.unit].filter((p) => p !== null && p !== undefined && p !== '').join(' '),
      status: r.status,
      interpretability: r.interpretability && r.interpretability !== 'full' ? r.interpretability : null,
    })),
  }
}

const exerciseCount = (session) => (Array.isArray(session?.exercises) ? session.exercises.length : Array.isArray(session?.items) ? session.items.length : 0)

export const trainingPlanSummary = (value) => {
  if (value === null || value === undefined) return { generated: false }
  if (!isObject(value)) return null
  const proposal = isObject(value.proposal) ? value.proposal : null
  const sessions = proposal && (Array.isArray(proposal.days) ? proposal.days : Array.isArray(proposal.sessions) ? proposal.sessions : null)
  if (sessions?.length) {
    return {
      generated: true,
      sessions: sessions.filter(isObject).map((s, i) => ({
        name: [s.name, s.day_name, s.title, s.day].find(isScalar) ?? null, index: i + 1, count: exerciseCount(s),
      })),
    }
  }
  const items = (Array.isArray(value.items) ? value.items : []).filter(isObject)
  if (items.length) {
    return { generated: true, exercises: items.map((x) => x.exercise_name || x.name).filter(isScalar), count: items.length }
  }
  return proposal ? null : { generated: false }
}

export const analysesSummary = (value) => {
  if (!isObject(value) || !isObject(value.steps)) return null
  return Object.entries(value.steps)
    .filter(([, s]) => isObject(s) && isScalar(s.status))
    .map(([step, s]) => ({ step, status: s.status, reason: isScalar(s.reason) ? String(s.reason) : null }))
}

/** `nutrition_targets.calories_kcal.value` / `recommendations[key=x].status` → section + readable subject. */
export const parseDiffPath = (path) => {
  const parts = String(path ?? '').match(/[^.[\]]+|\[[^\]]*\]/g) ?? []
  const [section, ...rest] = parts
  const subjects = rest.map((p) => (p.startsWith('[') ? { selector: p.slice(1, -1).split('=')[1] ?? null } : { key: p }))
  return { section: section ?? null, subjects }
}
